import React, {useMemo} from 'react';
import {ExUserRegistrationDetails} from 'hemwb-api';
import {tid} from '../../../../../testUtils';
import {
  ReloadOutlined,
  MailOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {Dropdown} from 'antd';
import type {MenuProps} from 'antd';
import styles from './UserRowActionMenu.module.scss';

export type UserRowActionMenuProps = {
  user: ExUserRegistrationDetails;
  adminExternalizationPage: boolean;
  onOpenDialog: (user: ExUserRegistrationDetails, action: string) => void;
  onOpenModal: (event: React.MouseEvent<HTMLElement>) => void;
  onOpenEnrollModal: (user: ExUserRegistrationDetails, event: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
};

export const UserRowActionMenu: React.FC<UserRowActionMenuProps> = ({
  user,
  adminExternalizationPage,
  onOpenDialog,
  onOpenModal,
  onOpenEnrollModal,
  children,
}) => {
  const disabled = !user || !user?.email;

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    if (!adminExternalizationPage) {
      // Non-admin view
      items.push({
        key: 'regenerate-code',
        icon: <ReloadOutlined />,
        label: 'Regenerate Code',
        disabled: disabled || user?.status === 'ACTIVE',
        onClick: (info) => {
          onOpenEnrollModal(user, info.domEvent as React.MouseEvent<HTMLElement>);
        },
        ...tid('btn', 'menu', 'regenerate-code'),
      });

      items.push({
        key: 'email',
        icon: <MailOutlined />,
        label: 'Email User',
        disabled,
        onClick: () => {
          if (!disabled && user?.email) {
            window.location.href = `mailto:${user.email}`;
          }
        },
        ...tid('btn', 'menu', 'email'),
      });
    } else {
      // Admin view
      items.push({
        key: 'activate',
        icon: <CheckOutlined />,
        label: 'Activate User',
        disabled: disabled || user?.status === 'ACTIVE' || user?.status === 'PENDING_REGISTRATION',
        onClick: () => {
          onOpenDialog(user, 'activate');
        },
        ...tid('btn', 'menu', 'activate'),
      });

      items.push({
        key: 'deactivate',
        icon: <CloseOutlined />,
        label: 'Deactivate User',
        disabled:
          disabled ||
          user?.status === 'INACTIVE' ||
          user?.status === 'PENDING_REGISTRATION' ||
          user?.status === 'PENDING_REVIEW',
        onClick: () => {
          onOpenDialog(user, 'deactivate');
        },
        ...tid('btn', 'menu', 'deactivate'),
      });

      items.push({
        key: 'reject',
        icon: <DeleteOutlined />,
        label: 'Reject Request',
        disabled:
          disabled ||
          user?.status === 'INACTIVE' ||
          user?.status === 'ACTIVE' ||
          user?.status === 'PENDING_REGISTRATION',
        onClick: () => {
          onOpenDialog(user, 'reject');
        },
        ...tid('btn', 'menu', 'reject'),
      });
    }

    // Common option for all views
    items.push({
      key: 'user-overview',
      icon: <UserOutlined />,
      label: 'User Overview',
      disabled,
      onClick: (info) => onOpenModal(info.domEvent as React.MouseEvent<HTMLElement>),
      ...tid('btn', 'menu', 'details'),
    });

    return items;
  }, [adminExternalizationPage, disabled, user, onOpenDialog, onOpenModal, onOpenEnrollModal]);

  return (
    <Dropdown
      menu={{items: menuItems}}
      trigger={['click']}
      overlayClassName={styles.actionsMenuDropdown}
      {...tid('user-row-action-menu', String(user.id))}
    >
      {children}
    </Dropdown>
  );
};

export default UserRowActionMenu;
